import { useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface PasswordFieldProps {
    name: string;
    label?: string;
    placeholder?: string;
    autoComplete?: 'current-password' | 'new-password';
    required?: boolean;
    disabled?: boolean;
    helpText?: string;
}

export function PasswordField({
    name,
    label,
    placeholder,
    autoComplete = 'current-password',
    required,
    disabled,
    helpText,
}: PasswordFieldProps) {
    const {
        register,
        formState: { errors },
    } = useFormContext();
    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    const error = errors[name];
    const message = typeof error?.message === 'string' ? t(error.message) : undefined;
    const errorId = `${name}-error`;
    const helpId = `${name}-help`;
    const describedBy = error ? errorId : helpText ? helpId : undefined;

    return (
        <Form.Group className="mb-3" controlId={name}>
            {label && (
                <Form.Label>
                    {label}
                    {required && (
                        <span className="field-required-mark" aria-hidden="true">
                            *
                        </span>
                    )}
                </Form.Label>
            )}
            <InputGroup hasValidation>
                <Form.Control
                    type={show ? 'text' : 'password'}
                    placeholder={placeholder}
                    autoComplete={autoComplete}
                    disabled={disabled}
                    isInvalid={!!error}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy}
                    {...register(name)}
                />
                <Button
                    variant="outline-secondary"
                    type="button"
                    disabled={disabled}
                    onClick={() => setShow((s) => !s)}
                    aria-pressed={show}
                    aria-label={show ? t('common.hide') : t('common.show')}
                >
                    {show ? t('common.hide') : t('common.show')}
                </Button>
                <Form.Control.Feedback type="invalid" id={errorId}>
                    {message}
                </Form.Control.Feedback>
            </InputGroup>
            {helpText && !error && (
                <Form.Text id={helpId} className="text-muted">
                    {helpText}
                </Form.Text>
            )}
        </Form.Group>
    );
}
